package org.zest.insan_360_website.components.widgets

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.UserSelect
import com.varabyte.kobweb.compose.dom.svg.Text
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Source
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Video
import org.zest.insan_360_website.components.layouts.BaseButtonModifier
import org.zest.insan_360_website.data.LanguageSettings
import org.zest.insan_360_website.data.page_states.UserPreferences
import kotlin.time.Duration.Companion.milliseconds
import org.zest.insan_360_website.data.localized

val videoModalLocalized = LanguageSettings.UserDashboardPage.VideoModal


@OptIn(DelicateApi::class)
@Composable
fun VideoModal(
    videoUrls: List<String>,
    title: String,
    finishButtonText: String,
    proceedButtonText: String,
    onProceed: () -> Unit,
    onCancel: (() -> Unit)? = null
) {
    val wordLibrary = LanguageSettings.UserDashboardPage.VideoModal
    val breakpoint = rememberBreakpoint()
    var currentVideoIndex by remember { mutableStateOf(0) }
    var isAllVideosFinished by remember { mutableStateOf(false) }
    val isMobile = breakpoint >= Breakpoint.ZERO && breakpoint < Breakpoint.SM
    
    val currentVideoUrl = videoUrls.getOrNull(currentVideoIndex) ?: ""
    // We use a simple hash of the URL to keep progress separate for different videos
    val storageKey = "video_progress_${currentVideoUrl.hashCode()}"
    
    // Dev environment bypass: allow proceeding after 2 seconds on localhost
    val isDevEnvironment = window.location.hostname == "localhost" || window.location.hostname == "127.0.0.1"
    var devBypassReady by remember { mutableStateOf(false) }
    
    if (isDevEnvironment && !isAllVideosFinished) {
        LaunchedEffect(Unit) {
            delay(2000.milliseconds)
            devBypassReady = true
        }
    }
    
    val canProceed = isAllVideosFinished || devBypassReady

    Column(
        Modifier
            .fillMaxWidth()
            .maxWidth(800.px)
            .userSelect(UserSelect.None)
            .gap(0.5.cssRem),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        SpanText(
            title,
            Modifier
                .fillMaxWidth()
                .fontSize(1.em)
                .fontWeight(FontWeight.Bold)
                .padding(if (isMobile) 1.em else 0.em)
                .textAlign(TextAlign.Center),
        )
        
        if (currentVideoUrl.isNotEmpty()) {
            key(currentVideoUrl) {
                Video(
                    attrs = Modifier
                        .fillMaxWidth()
                        .aspectRatio(16, 9)
                        .borderRadius(0.5.cssRem)
                        .backgroundColor(Colors.Black)
                        .toAttrs {
                            attr("controls", "true")
                            attr("controlsList", "nodownload")
                            attr("playsinline", "true")
                            ref { element ->

                                val savedTime = window.localStorage.getItem(storageKey)?.toDoubleOrNull()
                                if (savedTime != null && savedTime > 0.0) {
                                    element.currentTime = savedTime
                                }

                                element.addEventListener("timeupdate", {
                                    window.localStorage.setItem(storageKey, element.currentTime.toString())
                                })
                                
                                onDispose {}
                            }
                            
                            addEventListener("ended") {
                                window.localStorage.removeItem(storageKey)
                                
                                if (currentVideoIndex < videoUrls.size - 1) {
                                    currentVideoIndex++
                                } else {
                                    isAllVideosFinished = true
                                }
                            }
                        }
                ) {
                    Source(attrs = {
                        attr("src", currentVideoUrl)
                        attr("type", "video/mp4")
                    })
                    SpanText(wordLibrary.unsupported_video.localized)
                }
            }
        }
        
        if (videoUrls.size > 1) {
            SpanText(
                wordLibrary.video_counter.localized
                    .replace("{1}", (currentVideoIndex + 1).toString())
                    .replace("{2}", videoUrls.size.toString()),
                Modifier.fontSize(0.85.em).color(Colors.Gray)
            )
        }

        Box(
            Modifier.fillMaxWidth().padding(leftRight = if (isMobile) 1.cssRem else 0.px),
            contentAlignment = Alignment.Center
        ) {
            Button(
                onClick = { onProceed() },
                enabled = canProceed,
                modifier = BaseButtonModifier
                    .fillMaxWidth()
                    .padding(0.8.cssRem)
                    .thenIf(!canProceed, Modifier.cursor(Cursor.NotAllowed)),
            ) {
                SpanText(if (!canProceed) finishButtonText else proceedButtonText)
            }
        }
        
        if (onCancel != null) {
            SpanText(
                videoModalLocalized.watch_later[UserPreferences.language] ?: "Watch Later",
                Modifier
                    .fontSize(0.9.em)
                    .fontWeight(FontWeight.SemiBold)
                    .color(Colors.Gray)
                    .cursor(Cursor.Pointer)
                    .padding(0.75.em)
                    .onClick { onCancel() }
            )
        }
    }
}
